// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';

// Angular Material
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

// RxJS
import { map, Observable } from 'rxjs';

// Third-party
import { ROUTES } from 'src/routes';

// Local
import { Breadcrumb } from '@app/components/pagetitle/pagetitle.types';
import { AuthService } from '@app/core/auth/auth.service';
import { BreadcrumbService } from '@app/services/breadcrumbs/breadcrumbs.service';
import {
  IdentityClaims,
  IdentityClaimsService,
} from '@app/services/identity-claims/identity-claims.service';

@Component({
  selector: 'app-pagetitle',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatMenuModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './pagetitle.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./pagetitle.component.scss'],
})
export class PagetitleComponent implements OnInit {
  /**
   * @param router The Angular router used for navigation.
   */
  private router = inject(Router);

  /**
   * @param authService The auth service, which we use to bring the session user in from.
   */
  private readonly authService = inject(AuthService);

  /**
   * @param breadcrumbService The breadcrumb service, which we use to bring the where breadcrumb heirarchy in from.
   */
  private readonly breadcrumbService = inject(BreadcrumbService);
  breadcrumbs = this.breadcrumbService.breadcrumbs;

  /**
   * @param identityClaimsService The identity claims, which the user initials are derived from.
   */
  private readonly identityClaimsService = inject(IdentityClaimsService);

  /** Announces settings submenu state changes while focus remains on its trigger. */
  private readonly liveAnnouncer = inject(LiveAnnouncer);

  /**
   * The effective user's initials exposed as an Observable. Sourced from the identity claims rather
   * than the session user because the claims are emulation-aware: while a system admin emulates
   * another user, the button must show the emulated user's initials, not the admin's.
   */
  userInitials$: Observable<string> =
    this.identityClaimsService.identiyClaims$.pipe(
      map(
        (claims: IdentityClaims) =>
          (claims.given_name?.charAt(0) ?? '') +
          (claims.family_name?.charAt(0) ?? ''),
      ),
    );

  /**
   * A boolean value to determine whether to display the user-settings sub-menu.
   */
  showUserSettings = false;

  /**
   * A boolean to determine if the logged in user is non-subscribing
   */
  isNonSubscribingUser = false;

  ngOnInit() {
    this.isNonSubscribingUser = this.authService.isNonSubscribingUser;
  }

  navigateTo($event: Event, url: string) {
    $event.preventDefault();
    this.router.navigateByUrl(url);
  }

  isLinkActive(url: string): boolean {
    return this.breadcrumbService.activePrimaryUrl() === url;
  }

  /**
   * Toggles the settings submenu without closing the parent user menu and immediately announces
   * the changed state to screen-reader users.
   * @param event The settings trigger click event.
   */
  toggleUserSettings(event: Event): void {
    event.stopPropagation();
    this.showUserSettings = !this.showUserSettings;
    void this.liveAnnouncer.announce(
      `My Settings ${this.showUserSettings ? 'expanded' : 'collapsed'}`,
      'polite',
    );
  }

  /**
   * Stable tracking key for breadcrumb rendering.
   * Avoids DOM teardown/recreate when breadcrumb objects are re-instantiated.
   */
  breadcrumbTrackBy(index: number, breadcrumb: Breadcrumb): string {
    return `${breadcrumb.url}|${breadcrumb.label}|${breadcrumb.arrayIndex ?? index}`;
  }

  /**
   * A handler function for when the "My Information" button is clicked.
   */
  openMyInfo() {
    this.router.navigate([
      '/',
      { outlets: { 'sidepanel-info': [ROUTES.MY_INFORMATION] } },
    ]);
  }

  /**
   * A handler function for when the "Notification Options" button is clicked.
   */
  openNotificationOptions() {
    this.router.navigate([
      '/',
      { outlets: { 'sidepanel-info': [ROUTES.NOTIFICATION_OPTIONS] } },
    ]);
  }

  /**
   * A handler functiin for when the "My Saved Transactions" button is clciked.
   */
  openSavedTransactions() {
    this.router.navigate([
      '/',
      { outlets: { 'sidepanel-info': [ROUTES.SAVED_TRANSACTION_ACCESS] } },
    ]);
  }

  /**
   * A handler functin for when the "Log out" button is clicked.
   */
  logout() {
    this.router.navigate(['/', ROUTES.LOGOUT]);
  }
}
